import { simpleForm } from "@ts/core/SimpleForm";
import { AbstractSection } from "./AbstractSection";
import { ToolState } from "./EditTools";
import { Utils } from "@ts/core/Utils";
import { HtmlData } from "@ts/data/HtmlData";

export enum ListType {
    Dot,
    Number
}

export class HtmlSection extends AbstractSection {

    public constructor(section: HtmlData) {
        super(section);
        this.renderTarget.classList.add('html-wrapper');
    }

    protected onViewRender(): void {
        const data = this.data as HtmlData;
        this.renderTarget.innerHTML = data.htmlcode;
    }

    protected onEditRender(): void {

        let range: Range | null = null;

        const data = this.data as HtmlData;
        this.renderTarget.innerHTML = data.htmlcode;
        this.renderTarget.contentEditable = 'true';

        if (this.tbar) {
            this.tbar.add('Fet',
                `Markerad text till <strong>fet stil</strong>`,
                'strong', () => { this.toggleFormat('strong'); });

            this.tbar.add('Kursiv',
                `Markerad text till <em>kursiv stil</em>`,
                'em', () => { this.toggleFormat('em'); });

            this.tbar.add('Understuken',
                `Markerad text blir <u>understruken</u>`,
                'u', () => { this.toggleFormat('u'); });

            this.tbar.add('Länk',
                `Skapa en klickbar webblänk av markerad text. Exempelvis 'https://facebook.com' 
                eller om det är en E-post adress 'mailto:minepost@gmail.com'`,
                'a', async () => {
                    const selectedText = window.getSelection()?.toString();
                    if (selectedText != undefined) {
                        await simpleForm('Ange webbadress', 1, 'Adress', '', 'Min kommentar')
                            .then((text: string) => {
                                if (range) this.applyWebLink(range, selectedText, text);
                            });
                    }
                });

            this.tbar.add('Punktlista',
                `Skapa en lista där varje markerad rad startar med en punkt. Avsluta listan med dubbla Enter`,
                'ul', () => {
                    const selectedText = window.getSelection()?.toString();
                    if (selectedText != undefined && range) {
                        this.applyList(range,selectedText, ListType.Dot);
                    }
                });

            this.tbar.add('Numrerad lista', 
                `Skapa en lista där varje markerad rad är numrerad. Avsluta listan med dubbla Enter`,
                'ol', () => {
                    const selectedText = window.getSelection()?.toString();
                    if (selectedText != undefined && range) {
                        this.applyList(range,selectedText, ListType.Number);
                    }
                });

            this.tbar.setStateAll(ToolState.Disabled);
        }

        document.addEventListener('selectionchange', () => {
            const selection = document.getSelection();
            if (!selection || selection.rangeCount === 0) {
                return;
            }
            if (this.renderTarget.contains(selection.anchorNode)) {
                range = selection.getRangeAt(0);
                if (!range.collapsed) this.tbar?.setStateAll(ToolState.Normal);
            }
        });

    }

    protected parseContent(): void {
        (this.data as HtmlData).htmlcode  = this.renderTarget.innerHTML;
    }

    private toggleFormat(tagName: string): void {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            return;
        }

        const range = selection.getRangeAt(0);
        const tag = tagName.toLowerCase();


        if (this.isAlreadyFormatted(range, tag)) {
            this.unwrapFormat(range, tag);
        } else {
            this.wrapFormat(range, tag);
        }

        // Behåll markeringen
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private isAlreadyFormatted(range: Range, tagName: string): boolean {
        const startEl = this.closestTag(range.startContainer, tagName);
        const endEl = this.closestTag(range.endContainer, tagName);

        // Båda måste ligga i samma element av rätt typ
        this.tbar?.setState(tagName, !!(startEl && endEl && startEl === endEl) ? ToolState.Active : ToolState.Normal);
        return !!(startEl && endEl && startEl === endEl);
    }

    private closestTag(node: Node, tagName: string): HTMLElement | null {
        let current: Node | null = node;

        while (current && current !== document.body) {
            if (current.nodeType === Node.ELEMENT_NODE) {
                const el = current as HTMLElement;
                if (el.tagName.toLowerCase() === tagName) {
                    return el;
                }
            }
            current = current.parentNode;
        }
        return null;
    }

    private wrapFormat(range: Range, tagName: string): void {
        const wrapper = document.createElement(tagName);

        try {
            range.surroundContents(wrapper);
        } catch {
            // Markeringen går över flera noder
            const contents = range.extractContents();
            wrapper.appendChild(contents);
            range.insertNode(wrapper);
        }
        this.tbar?.setState(tagName, ToolState.Active);
    }

    private unwrapFormat(range: Range, tagName: string): void {
        const formatted = this.closestTag(range.commonAncestorContainer, tagName);
        if (!formatted) return;

        const parent = formatted.parentNode;
        if (!parent) return;

        // Flytta ut alla barn
        while (formatted.firstChild) {
            parent.insertBefore(formatted.firstChild, formatted);
        }
        parent.removeChild(formatted);
        parent.normalize();
        this.tbar?.setState(tagName, ToolState.Normal);
    }

    private applyWebLink(range: Range, selectedText: string, url: string): void {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const a = document.createElement("a");
        a.href = url;
        a.textContent = selectedText;

        range.deleteContents();
        range.insertNode(a);

        // Flytta markören efter länken
        range.setStartAfter(a);
        range.setEndAfter(a);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private applyList(range: Range, selectedText: string, type: ListType): void {

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const list = document.createElement(type === ListType.Dot ? 'ul' : 'ol');
        const rows = Utils.splitTextIntoRows(selectedText);
        if (rows.length) {
            for (let i = 0; i < rows.length; i++) {
                const li = document.createElement('li');
                li.innerText = rows[i];
                list.appendChild(li);
            }


            range.deleteContents();
            range.insertNode(list);

            // Flytta markören efter länken
            range.setStartAfter(list);
            range.setEndAfter(list);
            selection.removeAllRanges();
            selection.addRange(range);

            (list.firstElementChild as HTMLElement).focus();
        }
    }
}